import { LoadingOverlay } from '@mantine/core'
import { generateNavigationMenu } from 'App/Map/LayerNav/Layers/generateNavigationMenu/generateNavigationMenu'
import { setGroupSelection } from 'App/Map/PanelDetails/OverviewCharts/SectionTable/utils'
import { MultiSelect } from 'components/MultiSelect/MultiSelect'
import type { TOptions } from 'components/MultiSelect/MultiSelect.types'
import { translate } from 'i18n/i18n'
import { useCallback, useMemo, useRef } from 'react'
import { selectedValueGroupsStore } from 'stores/selectedValueGroupsStore/selectedValueGroupsStore'
import { capitalizeFirstLetter } from 'utils/capitalizeFirstLetter'
import { PropertySectionTables } from './PropertySectionTables'

export const groupedKeys: Record<string, string[]> = {
  irrigation: [
    'irrigation_activity_pressure',
    'irrigation_activity_zones',
    'irrigation_activity_flow_meters',
    'irrigation_activity',
  ],
}

export const OverviewCharts = ({
  refreshKey,
  containerHeight,
  containerWidth,
  preventFetch,
  selectedValueGroups,
}: {
  refreshKey?: number
  containerHeight: number
  containerWidth: number
  preventFetch: boolean
  selectedValueGroups: ReturnType<typeof selectedValueGroupsStore.getState>['selectedValueGroups']
}) => {
  const navigationMenu = generateNavigationMenu({ selectedValueGroups })

  const valuesToOmit = [
    'conditions',
    'wind_machine',
    'equipment_status',
    'grape_powdery_mildew',
    'cherry_powdery_mildew',
  ]

  const pestKeyTypes = ['degree_days_insect_id_', 'trap_catches_insect_id_', 'larval_trend_insect_id_']

  const layersActive = useMemo(
    () =>
      navigationMenu
        .filter((section) => section.childrenChecked === 'SOME' || section.childrenChecked === 'ALL')
        .map((section) =>
          section.key === 'insects' ? 'Pests' : section.key.split('-').map(capitalizeFirstLetter).join(' '),
        )
        .filter((label) => label !== translate.phrases.banyanApp('Equipment')),
    [navigationMenu],
  )

  const { valueGroupOptions, selectedValueGroupsArray } = useMemo(() => {
    const flatMapValueGroups = (valueGroups: typeof navigationMenu) =>
      valueGroups
        .flatMap((section) =>
          section.children.map((valueGroup) => ({
            label: valueGroup.translatedTitle,
            value: valueGroup.key,
            checked: valueGroup.checked,
          })),
        )
        .sort((a, b) => a.label.localeCompare(b.label))

    const valueGroupOptions = flatMapValueGroups(navigationMenu).filter(
      (valueGroup) => !valuesToOmit.includes(valueGroup.value),
    )

    const selectedValueGroupsArray = valueGroupOptions.filter(
      (valueGroup) => valueGroup.checked === true && !valuesToOmit.includes(valueGroup.value),
    )

    return { valueGroupOptions, selectedValueGroupsArray }
  }, [navigationMenu])

  const expandPestKeys = (selectedKeys: string[]): string[] => {
    const expanded = [...selectedKeys]

    selectedKeys.forEach((key) => {
      if (key.startsWith('insect_id_')) {
        const number = key.replace('insect_id_', '')

        pestKeyTypes.forEach((type) => {
          const extraKey = `${type}${number}`

          if (!expanded.includes(extraKey)) expanded.push(extraKey)
        })
      }
    })

    return expanded
  }

  const updateSelections = useCallback(
    (currentKeys: string[], selectedKeys: string[], currentSelection: Record<string, number | false>) => {
      const timestamp = +new Date()
      const updatedSelection = { ...currentSelection }

      setGroupSelection(groupedKeys.irrigation, selectedKeys, timestamp, updatedSelection)

      currentKeys
        .filter((key) => key.startsWith('insect_id_'))
        .forEach((key) => {
          const number = key.split('insect_id_')[1]
          // Consider selected if *any* pest in group is chosen
          const pestKeys = pestKeyTypes.map((type) => `${type}${number}`)
          const groupSelected = pestKeys.some((k) => selectedKeys.includes(k))

          pestKeys.forEach((k) => {
            updatedSelection[k] = groupSelected ? timestamp : false

            if (groupSelected && !selectedKeys.includes(k)) {
              selectedKeys.push(k)
            }
          })
        })

      // Non-grouped keys
      currentKeys.forEach((key) => {
        if (
          !groupedKeys.irrigation.includes(key) &&
          !key.startsWith('insect_id_') &&
          !['degree_days_insect_id_', 'trap_catches_insect_id_', 'larval_trend_insect_id_'].some((prefix) =>
            key.startsWith(prefix),
          )
        ) {
          updatedSelection[key] = selectedKeys.includes(key) ? timestamp : false
        }
      })

      selectedKeys.forEach((key) => {
        if (!currentKeys.includes(String(key))) {
          updatedSelection[key] = timestamp
        }
      })

      return updatedSelection
    },
    [],
  )

  const prevSelectedKeys = useRef<string[]>([])

  const handleMultiSelectChange = useCallback(
    (selectedValues: TOptions[]) => {
      const currentKeys = Object.keys(selectedValueGroups).map(String)

      let selectedKeys = selectedValues.map((v) => String(v.value))

      selectedKeys = expandPestKeys(selectedKeys)

      prevSelectedKeys.current = selectedKeys

      if (selectedKeys.length === 0) {
        const allKeys = [
          ...currentKeys,
          ...currentKeys
            .filter((k) => k.startsWith('insect_id_'))
            .flatMap((key) => {
              const number = key.replace('insect_id_', '')

              return [`insect_id_${number}`, ...pestKeyTypes].map((type) => `${type}${number}`)
            }),
        ]

        const updatedSelection: Record<string, number | false> = { ...selectedValueGroups }

        allKeys.forEach((k) => {
          updatedSelection[k] = false
        })

        selectedValueGroupsStore.setState((s) => ({
          ...s,
          selectedValueGroups: updatedSelection,
        }))

        return
      }

      const updatedSelection = updateSelections(currentKeys, selectedKeys, selectedValueGroups)

      selectedValueGroupsStore.setState((s) => ({
        ...s,
        selectedValueGroups: updatedSelection,
      }))
    },
    [selectedValueGroups, updateSelections],
  )

  return (
    <>
      <LoadingOverlay visible={false} />
      <div css={{ display: 'flex', flexDirection: 'column', height: containerHeight }}>
        <div css={{ padding: '10px' }}>
          <MultiSelect
            data={valueGroupOptions}
            onChange={handleMultiSelectChange}
            selectedData={selectedValueGroupsArray}
          />
        </div>
        <PropertySectionTables
          refreshKey={refreshKey}
          containerWidth={containerWidth}
          preventFetch={preventFetch}
          selectedValueGroups={selectedValueGroups}
          selectedValueGroupsArray={selectedValueGroupsArray}
          layersActive={layersActive}
        />
      </div>
    </>
  )
}
